import React, { useState, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import { useTranslation } from "react-i18next";
import DefaultFooter from "../components/default-footer";
import DefaultHeader from "../components/default-header";
import { useSeo } from "../components/useSeo";
import { breadcrumbJsonLd } from "../components/seoData";
import { API_URL } from "../config";
import Header from '../components/header';

interface FormDataState {
  name: string;
  email: string;
  phone: string;
  title: string;
  description: string;
}

interface MessageState {
  type: 'success' | 'error' | '';
  text: string;
}

const Contact: React.FC = () => {
  const { t } = useTranslation();

   const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo({
    title: "Contact Us | SilverJarz Travel & Tourism",
    description: "Get in touch with SilverJarz for visa applications, flight tickets, tours, and travel services in Oman — call, WhatsApp, or email us, available 24/7.",
    canonical: `${origin}/contact`,
    jsonLd: breadcrumbJsonLd([
      { name: "Home", url: `${origin}/` },
      { name: "Contact", url: `${origin}/contact` },
    ]),
  });


  const [formData, setFormData] = useState<FormDataState>({
    name: '',
    email: '',
    phone: '',
    title: '',
    description: ''
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<MessageState>({ type: '', text: '' });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${API_URL}/ticket`, {
        name: formData.name,
        email: formData.email,
        title: formData.title,
        description: formData.description
      });

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Ticket sent successfully! Ticket number: ${response.data.data.ticket_number}`
        });
        setFormData({ name: '', email: '', phone: '', title: '', description: '' });
      }
    } catch (error: any) {
      if (error.response && error.response.data) {
        const resData = error.response.data;
        if (resData.errors) {
          const firstErrorKey = Object.keys(resData.errors)[0];
          setMessage({
            type: 'error',
            text: resData.errors[firstErrorKey][0]
          });
        } else {
          setMessage({
            type: 'error',
            text: resData.message || 'An error occurred while sending.'
          });
        }
      } else {
        setMessage({
          type: 'error',
          text: 'Unable to reach the server. Please check your connection.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .contact-form-wrap {
          background: #fff;
          border-radius: 20px;
          padding: 40px 44px;
          box-shadow: 0 8px 48px rgba(41,75,90,0.10);
          height: 100%;
        }
        .cf-title {
          font-size: 22px;
          font-weight: 700;
          color: #1a2f38;
          margin-bottom: 6px;
        }
        .cf-subtitle {
          font-size: 14px;
          color: #8aa5b0;
          margin-bottom: 32px;
        }
        .cf-field {
          position: relative;
          margin-bottom: 20px;
        }
        .cf-field label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: #294b5a;
          margin-bottom: 8px;
        }
        .cf-field input,
        .cf-field textarea {
          width: 100%;
          padding: 13px 18px;
          border: 1.5px solid #dde6ea;
          border-radius: 10px;
          font-size: 14.5px;
          font-family: 'DM Sans', sans-serif;
          color: #1a2f38;
          background: #f7fafc;
          outline: none;
          transition: border-color 0.25s, background 0.25s, box-shadow 0.25s;
          resize: none;
        }
        .cf-field input:focus,
        .cf-field textarea:focus {
          border-color: #294b5a;
          background: #fff;
          box-shadow: 0 0 0 4px rgba(41,75,90,0.08);
        }
        .cf-field input::placeholder,
        .cf-field textarea::placeholder {
          color: #b0c4cc;
        }
        .cf-field-icon { position: relative; }
        .cf-field-icon input { padding-left: 44px; }
        .cf-field-icon .cf-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #294b5a;
          opacity: 0.5;
          font-size: 16px;
          pointer-events: none;
        }
        .cf-divider {
          height: 2px;
          background: linear-gradient(90deg, #294b5a 0%, #D3BE76 100%);
          border-radius: 2px;
          margin-bottom: 28px;
          width: 60px;
        }
        .cf-submit {
          width: 100%;
          padding: 15px 32px;
          background: #294b5a;
          color: #fff;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
          margin-top: 8px;
        }
        .cf-submit:hover {
          background: #1a2f38;
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(41,75,90,0.28);
        }
        .cf-submit:disabled {
          background: #8aa5b0;
          cursor: not-allowed;
          transform: none;
        }
        .cf-alert {
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 20px;
        }
        .cf-alert-success {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }
        .cf-alert-error {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .ci-card {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          background: #fff;
          border-radius: 14px;
          padding: 18px 20px;
          box-shadow: 0 4px 20px rgba(41,75,90,0.07);
          border-left: 3px solid transparent;
          transition: border-color 0.25s, transform 0.25s, box-shadow 0.25s;
          cursor: default;
        }
        .ci-card:hover {
          border-left-color: #D3BE76;
          transform: translateX(4px);
          box-shadow: 0 8px 32px rgba(41,75,90,0.13);
        }
        .ci-card__icon {
          width: 42px; height: 42px; min-width: 42px;
          border-radius: 11px;
          background: rgba(41,75,90,0.08);
          display: flex; align-items: center; justify-content: center;
          font-size: 17px; color: #294b5a;
        }
        .ci-card__label {
          font-size: 10px; 
          font-weight: 700;
          color: #94b0bc; 
          margin-bottom: 4px;
        }
        .ci-card__value {
          font-size: 14.5px; font-weight: 600;
          color: #1a2f38; line-height: 1.4;
        }
        .ci-card__link {
          font-size: 12.5px; color: #294b5a; font-weight: 500;
          text-decoration: none; display: inline-flex;
          align-items: center; gap: 4px; margin-top: 3px;
          transition: color 0.2s;
        }
        .ci-card__link:hover { color: #D3BE76; }
        .ci-card__link svg {
          width: 11px; height: 11px;
          stroke: currentColor; fill: none; stroke-width: 2;
        }

        .contact-map-wrap {
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(41,75,90,0.12);
          border: 1.5px solid #e8eef1;
          margin-top: 40px;
        }

        @media (max-width: 768px) {
          .contact-form-wrap { padding: 28px 20px; }
        }
      `}</style>

      <div className="header-margin"></div>
      <Header />

      <section className="layout-pt-md">
        <div className="container">
          <div className="row x-gap-40 y-gap-30">

            {/* Contact Info */}
            <div className="col-lg-5 col-12">
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                <div style={{ width: "36px", height: "2px", background: "linear-gradient(90deg,#294b5a,#D3BE76)", borderRadius: "2px" }}></div>
                <span style={{ fontSize: "11px", fontWeight: 700, color: "#294b5a" }}>{t("contact")}</span>
              </div>
              <div style={{ fontSize: "22px", fontWeight: 700, color: "#1a2f38", marginBottom: "6px" }}>
                {t("contactTitle")}
              </div>
              <p style={{ fontSize: "13.5px", color: "#8aa5b0", lineHeight: 1.7, marginBottom: "24px" }}>
                {t("contactSubtitle")}
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div className="ci-card">
                  <div className="ci-card__icon">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="#294b5a">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.121 1.532 5.853L.057 23.571a.75.75 0 00.921.921l5.718-1.475A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.723 9.723 0 01-4.964-1.357l-.355-.212-3.693.952.972-3.596-.231-.371A9.722 9.722 0 012.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="ci-card__label">{t("whatsapp")}</div>
                    <div className="ci-card__value">{t("whatsappDesc")}</div>
                    <a href="https://wa.me/96893571" className="ci-card__link" target="_blank" rel="noreferrer">
                      {t("startChat")}
                      <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </a>
                  </div>
                </div>

                <div className="ci-card">
                  <div className="ci-card__icon">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="ci-card__label">{t("callUs")}</div>
                    <div className="ci-card__value">
                      <a href="tel:+96893579571" style={{ color: "#1a2f38", textDecoration: "none" }}>+96893579571</a>
                    </div>
                    <div style={{ fontSize: "12px", color: "#94b0bc", marginTop: "2px" }}>{t("available247")}</div>
                  </div>
                </div>

                <div className="ci-card">
                  <div className="ci-card__icon">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <div>
                    <div className="ci-card__label">{t("emailUs")}</div>
                    <div className="ci-card__value">
                      <a href="mailto:info@silverjarz.com" style={{ color: "#1a2f38", textDecoration: "none" }}>
                         info@silverjarz.com
                      </a>
                    </div>
                    <div style={{ fontSize: "12px", color: "#94b0bc", marginTop: "2px" }}>{t("emailResponse")}</div>
                  </div>
                </div>

                <div className="ci-card">
                  <div className="ci-card__icon">
                    <i className="icon-location-2" style={{ fontSize: "17px" }}></i>
                  </div>
                  <div>
                    <div className="ci-card__label">{t("location")}</div>
                    <div className="ci-card__value">Khasab Musandam Oman</div>
                    <a href="https://maps.google.com/?q=SILVER+JARZ+TRAVEL+TOURISM" className="ci-card__link" target="_blank" rel="noreferrer">
                      {t("viewOnMap")}
                      <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </a>
                  </div>
                </div>

                <div className="ci-card">
                  <div className="ci-card__icon">
                    <i className="icon-clock" style={{ fontSize: "17px" }}></i>
                  </div>
                  <div>
                    <div className="ci-card__label">{t("hours")}</div>
                    <div className="ci-card__value">Mon – Sun, 9:00 AM – 10:00 PM</div>
                    <div style={{ fontSize: "12px", color: "#94b0bc", marginTop: "2px" }}>{t("whatsapp247")}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="col-lg-7 col-12">
              <div className="contact-form-wrap">
                <div className="cf-title">{t("sendMessage")}</div>
                <p className="cf-subtitle">{t("teamResponse")}</p>
                <div className="cf-divider"></div>

                {message.text && (
                  <div className={`cf-alert ${message.type === 'success' ? 'cf-alert-success' : 'cf-alert-error'}`}>
                    {message.text}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="row x-gap-16">
                    <div className="col-md-6 col-12">
                      <div className="cf-field">
                        <label>{t("fullName")} <span style={{ color: "#D3BE76" }}>*</span></label>
                        <div className="cf-field-icon">
                          <i className="icon-user cf-icon"></i>
                          <input 
                            type="text" 
                            name="name" 
                            value={formData.name} 
                            onChange={handleChange} 
                            placeholder={t("fullNamePlaceholder")} 
                            required 
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-12">
                      <div className="cf-field">
                        <label>{t("emailAddress")} <span style={{ color: "#D3BE76" }}>*</span></label>
                        <div className="cf-field-icon">
                          <i className="icon-mail cf-icon"></i>
                          <input 
                            type="email" 
                            name="email" 
                            value={formData.email} 
                            onChange={handleChange} 
                            placeholder={t("emailPlaceholder")} 
                            required 
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row x-gap-16">
                    <div className="col-md-6 col-12">
                      <div className="cf-field">
                        <label>{t("phoneNumber")}</label>
                        <div className="cf-field-icon">
                          <i className="icon-phone cf-icon"></i>
                          <input 
                            type="tel" 
                            name="phone" 
                            value={formData.phone} 
                            onChange={handleChange} 
                            placeholder={t("phonePlaceholder")} 
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-12">
                      <div className="cf-field">
                        <label>{t("subject")} <span style={{ color: "#D3BE76" }}>*</span></label>
                        <div className="cf-field-icon">
                          <i className="icon-info cf-icon"></i>
                          <input 
                            type="text" 
                            name="title" 
                            value={formData.title} 
                            onChange={handleChange} 
                            placeholder={t("subjectPlaceholder")} 
                            required 
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="cf-field">
                    <label>{t("yourMessage")} <span style={{ color: "#D3BE76" }}>*</span></label>
                    <textarea 
                      rows={7} 
                      name="description" 
                      value={formData.description} 
                      onChange={handleChange} 
                      placeholder={t("messagePlaceholder")} 
                      required
                    ></textarea>
                  </div>

                  <button type="submit" className="cf-submit" disabled={loading}>
                    {loading ? 'Sending...' : t("sendMessageBtn")}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </button>
                </form>
              </div>
            </div>

          </div>

          <div className="contact-map-wrap mb-4">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14321.58786762492!2d56.231417680366285!3d26.18376197933331!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef6f5719ea09f07%3A0x3d6c9ca9eee51a97!2zU0lMVkVSIEpBUlogVFJBVkVMICYgVE9VUklTTSzYp9mE2KzYsdiyINin2YTZgdi22Yog2YTZhNiz2YHYsSDZiNin2YTYs9mK2KfYrdmH!5e0!3m2!1sen!2seg!4v1692953461363!5m2!1sen!2seg"
              loading="lazy"
              style={{ width: "100%", height: "380px", border: 0, display: "block" }}
            />
          </div>

        </div>
      </section>

      <DefaultFooter />
    </>
  );
};

export default Contact;